import type { GradeMappingSchema } from '@repo/schemas/grade-scale/gradeScale';

import { CheckCircle2Icon, ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button/ButtonGroup';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/dialog';

import { GradeScaleOptionsMenu } from './GradeScaleOptionsMenu';

export interface GradeScaleSaveButtonProps {
  formId: string;
  getGradeMapping: () => GradeMappingSchema | null;
  setGradeMapping: (gradeMapping: GradeMappingSchema | null) => void;
  disabled?: boolean;
  showGradeOptions?: boolean;
  label?: string;
  /**
   * When false, picking a scale only stores it on the form instead of
   * submitting — the main button stays the only way to save.
   */
  submitOnSelect?: boolean;
}

export function GradeScaleSaveButton(gradeScaleSaveButtonProps: GradeScaleSaveButtonProps) {
  const { formId, getGradeMapping, setGradeMapping, disabled = false, showGradeOptions = true, label = 'Zapisz', submitOnSelect = true } = gradeScaleSaveButtonProps;
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);

  const submitForm = () => {
    const form = document.getElementById(formId);
    if (form instanceof HTMLFormElement) {
      form.requestSubmit();
    }
  };

  const saveWith = (gradeMapping: GradeMappingSchema | null) => {
    setGradeMapping(gradeMapping);
    if (submitOnSelect) {
      submitForm();
    }
  };

  const stageSave = (gradeMapping: GradeMappingSchema | null, label: string) => {
    setGradeMapping(gradeMapping);
    if (submitOnSelect) {
      setPendingLabel(label);
    }
  };

  if (!showGradeOptions) {
    return (
      <Button type="button" variant="primary" onClick={submitForm} disabled={disabled}>
        <CheckCircle2Icon />
        {label}
      </Button>
    );
  }

  return (
    <>
      <AlertDialog open={pendingLabel !== null} onOpenChange={(open) => !open && setPendingLabel(null)}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Zapisać i zastosować skalę ocen?</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="p-4">
            <AlertDialogDescription>
              {getGradeMapping() ? `Skala „${pendingLabel}” zostanie zastosowana, a zmiany zapisane.` : 'Ocenianie zostanie wyłączone, a zmiany zapisane.'}
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter className="bg-card border-t">
            <AlertDialogCancel onClick={() => setPendingLabel(null)}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setPendingLabel(null);
                submitForm();
              }}
            >
              Zapisz
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ButtonGroup>
        <Button type="button" variant="primary" onClick={submitForm} disabled={disabled}>
          <CheckCircle2Icon />
          {label}
        </Button>
        <GradeScaleOptionsMenu
          currentGradeMapping={getGradeMapping()}
          onSelectPreset={stageSave}
          onSelectCustom={saveWith}
          removeLabel={submitOnSelect ? 'Zapisz bez oceniania' : 'Usuń ocenianie'}
          removeDisabled={!submitOnSelect && !getGradeMapping()}
        >
          <Button type="button" variant="primary" aria-label="Opcje zapisu z ocenianiem" className="group px-2" disabled={disabled}>
            <ChevronDownIcon className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </Button>
        </GradeScaleOptionsMenu>
      </ButtonGroup>
    </>
  );
}
